import { useMutation, useQuery } from '@apollo/client';
import {
  AlertCircle,
  ExternalLink,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';

import { GET_KB_WEBSITE_URLS_QUERY } from '@/apollo/schemas/knowledgeBase/getKbWebsiteUrls';
import { REMOVE_KB_WEBSITE_URL } from '@/apollo/schemas/knowledgeBase/removeKbWebsiteUrl';
import { SYNC_KB_WEBSITE_URLS } from '@/apollo/schemas/knowledgeBase/syncKbWebsiteUrls';
import { UPDATE_KB_WEBSITE_URL } from '@/apollo/schemas/knowledgeBase/updateKbWebsiteUrl';
import { AdminActionWrapper } from '@/components/common/AdminActionWrapper';
import KbSyncResults from '@/components/knowledgeBase/KbSyncResults';
import KbUrlDialog from '@/components/knowledgeBase/KbUrlDialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { useKbAuth } from '@/hooks/useKbAuth';
import { IKbSyncResult, IKbWebsiteUrl } from '@/types/knowledgeBase.types';
import {
  formatKbDate,
  isAbortError,
  isKbOk,
  KB_SYNC_TIMEOUT_MS,
  kbErrorMessage,
  withKbTimeout,
} from '@/utils/knowledgeBase';

const KbWebsiteUrls = () => {
  const { toast } = useToast();
  const { identityToken, kbContext } = useKbAuth();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<IKbWebsiteUrl | null>(null);
  const [pendingDelete, setPendingDelete] = useState<IKbWebsiteUrl | null>(
    null,
  );
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [syncOutcome, setSyncOutcome] = useState<{
    results: IKbSyncResult[];
    errorMessage: string | null;
  } | null>(null);

  const { data, loading, error, refetch } = useQuery(
    GET_KB_WEBSITE_URLS_QUERY,
    {
      skip: !identityToken,
      fetchPolicy: 'cache-and-network',
      context: kbContext(),
    },
  );

  const payload = data?.getKbWebsiteUrls;
  const urls: IKbWebsiteUrl[] = payload?.urls ?? [];

  // A 403/404 arrives as statusCode inside a 200 OK, so `error` alone is not enough.
  const loadError = error
    ? error.message
    : payload && !isKbOk(payload)
      ? kbErrorMessage(payload, 'Could not load knowledge sources.')
      : null;

  const [updateUrl] = useMutation(UPDATE_KB_WEBSITE_URL);
  const [removeUrl, { loading: removing }] = useMutation(REMOVE_KB_WEBSITE_URL);
  const [syncUrls, { loading: syncing }] = useMutation(SYNC_KB_WEBSITE_URLS);

  const handleToggleActive = async (row: IKbWebsiteUrl) => {
    setTogglingId(row.id);
    try {
      // `isActive` only — sending `id` alone is rejected as a 400.
      const { data: toggleData } = await updateUrl({
        variables: { input: { id: row.id, isActive: !row.isActive } },
        context: kbContext(),
      });

      const result = toggleData?.updateKbWebsiteUrl;
      if (!isKbOk(result)) {
        toast({
          variant: 'destructive',
          title: 'Could not change status',
          description: kbErrorMessage(result),
        });
        return;
      }

      toast({
        variant: 'success',
        title: row.isActive ? 'Source paused' : 'Source activated',
        description: row.isActive
          ? `${row.url} will be skipped on the next sync.`
          : `${row.url} will be crawled on the next sync.`,
      });
      await refetch();
    } catch (toggleError) {
      toast({
        variant: 'destructive',
        title: 'Could not change status',
        description: (toggleError as Error).message,
      });
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;

    try {
      const { data: removeData } = await removeUrl({
        variables: { input: { id: pendingDelete.id } },
        context: kbContext(),
      });

      const result = removeData?.removeKbWebsiteUrl;
      if (!isKbOk(result)) {
        toast({
          variant: 'destructive',
          title: 'Could not remove source',
          description: kbErrorMessage(result),
        });
        return;
      }

      toast({
        variant: 'success',
        title: 'Source removed',
        description: `${pendingDelete.url} is no longer a knowledge source.`,
      });
      setPendingDelete(null);
      await refetch();
    } catch (deleteError) {
      toast({
        variant: 'destructive',
        title: 'Could not remove source',
        description: (deleteError as Error).message,
      });
    }
  };

  const handleSync = async () => {
    // Cleared up front so a failure never leaves rows from a previous sync on screen.
    setSyncOutcome(null);

    const failSync = (description: string) => {
      setSyncOutcome({ results: [], errorMessage: description });
      toast({ variant: 'destructive', title: 'Sync failed', description });
    };

    try {
      // Runs inline over every active source — needs the long timeout.
      const { data: syncData } = await withKbTimeout(
        KB_SYNC_TIMEOUT_MS,
        (fetchOptions) => syncUrls({ context: kbContext(fetchOptions) }),
      );

      const result = syncData?.syncKbWebsiteUrls;
      // On a non-200 the results array is empty, so show the message instead.
      if (!isKbOk(result)) {
        failSync(kbErrorMessage(result, 'The sync did not run.'));
        return;
      }

      // Outcomes are per-source, so they are rendered as rows rather than a toast.
      setSyncOutcome({ results: result.results ?? [], errorMessage: null });
      await refetch();
    } catch (syncError) {
      // Apollo wraps the abort in an ApolloError, so check the networkError too.
      failSync(
        isAbortError(syncError)
          ? `The sync did not finish within ${KB_SYNC_TIMEOUT_MS / 1000}s. It may still be running — refresh in a moment to check.`
          : (syncError as Error).message,
      );
    }
  };

  const activeCount = urls.filter((row) => row.isActive).length;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-muted-foreground">
          {loading && !urls.length
            ? 'Loading sources…'
            : `${urls.length} source${urls.length === 1 ? '' : 's'} · ${activeCount} active`}
        </p>

        <div className="flex items-center gap-2">
          <AdminActionWrapper>
            <Button
              variant="outline"
              onClick={handleSync}
              disabled={syncing || !activeCount}
              title={
                activeCount ? undefined : 'Add an active source to run a sync'
              }
            >
              {syncing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {syncing ? 'Syncing…' : 'Run sync'}
            </Button>
          </AdminActionWrapper>

          <AdminActionWrapper>
            <Button
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add URL
            </Button>
          </AdminActionWrapper>
        </div>
      </div>

      {syncing && (
        <div className="flex items-center gap-2 rounded-lg border bg-card px-4 py-3 mb-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Crawling {activeCount} active source
          {activeCount === 1 ? '' : 's'}. This can take up to 30 seconds — leave
          this page open.
        </div>
      )}

      {syncOutcome && (
        <KbSyncResults
          results={syncOutcome.results}
          errorMessage={syncOutcome.errorMessage}
          onDismiss={() => setSyncOutcome(null)}
        />
      )}

      {loadError && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 mb-4">
          <AlertCircle className="h-4 w-4 mt-0.5 text-destructive shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-destructive">
              Could not load knowledge sources
            </p>
            <p className="text-muted-foreground">{loadError}</p>
          </div>
        </div>
      )}

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>URL</TableHead>
              <TableHead className="w-[120px]">Active</TableHead>
              <TableHead className="w-[180px]">Last updated</TableHead>
              <TableHead className="w-[100px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!urls.length && !loading && !loadError && (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="h-24 text-center text-muted-foreground"
                >
                  No knowledge sources yet. Add a URL to get started.
                </TableCell>
              </TableRow>
            )}

            {urls.map((row) => (
              <TableRow
                key={row.id}
                className={row.isActive ? '' : 'opacity-60'}
              >
                <TableCell className="max-w-[420px]">
                  <a
                    href={row.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-start gap-1.5 hover:underline break-all"
                  >
                    {row.url}
                    <ExternalLink className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                  </a>
                </TableCell>

                <TableCell>
                  <div className="flex items-center gap-2">
                    <AdminActionWrapper>
                      <Switch
                        checked={row.isActive}
                        disabled={togglingId === row.id}
                        onCheckedChange={() => handleToggleActive(row)}
                        aria-label={
                          row.isActive
                            ? `Pause ${row.url}`
                            : `Activate ${row.url}`
                        }
                      />
                    </AdminActionWrapper>
                    {togglingId === row.id && (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                    )}
                  </div>
                </TableCell>

                <TableCell className="text-sm text-muted-foreground">
                  {formatKbDate(row.updatedAt)}
                </TableCell>

                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <AdminActionWrapper>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Edit ${row.url}`}
                        onClick={() => {
                          setEditing(row);
                          setDialogOpen(true);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </AdminActionWrapper>
                    <AdminActionWrapper>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Remove ${row.url}`}
                        className="text-destructive hover:text-destructive"
                        onClick={() => setPendingDelete(row)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </AdminActionWrapper>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <KbUrlDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        onSaved={() => refetch()}
      />

      <Dialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <DialogContent className="sm:max-w-[440px] bg-white dark:bg-foreground dark:text-white">
          <DialogHeader>
            <DialogTitle>Remove knowledge source</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground break-all">
            <span className="font-medium text-foreground">
              {pendingDelete?.url}
            </span>{' '}
            will be removed from the knowledge base. To keep it but stop
            crawling it, switch it to inactive instead.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={removing}
              onClick={() => setPendingDelete(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={removing}
              onClick={handleDelete}
            >
              {removing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KbWebsiteUrls;
